import type {
  ClassId,
  EquipSlot,
  LookDef,
  RareTier,
  SkillId,
  ToolKind,
  Weather,
  ZoneId,
  ZoneLink,
  SpellSchool,
} from "./content/types";
import type { TownHousing } from "./sim/townQuests";
import type { TownQuestsView } from "./sim/townQuestLedger";

// Wire protocol between browser and authoritative server. JSON over one WebSocket.
// Client sends intents; the server validates everything and answers with state.

export interface ItemStack {
  item: string;
  count: number;
}
export type Slot = ItemStack | null;

/** A map pin (docs/map-pins.md): a mark of your own on a zone's map. */
export interface MapPin {
  id: number;
  x: number;
  z: number;
  /** a name you gave it (double-click it on the map) */
  name?: string;
}

/** `belt`: the tool belt (docs/tools.md), its slots in TOOL_KINDS order (sim/toolBelt.ts). */
export type ContainerRef = { kind: "inv" } | { kind: "belt" } | { kind: "chest"; id: number };

export type ClientMsg =
  /** `sprint`: the step was sprinted (docs/stamina.md): the budget allows it while stamina lasts. */
  | {
      t: "move";
      x: number;
      z: number;
      y: number;
      ry: number;
      moving: boolean;
      seq: number;
      sprint?: boolean;
      /**
       * The latest `correct` the client has applied (its `n`): a move made before it is dropped
       * (the move fence, docs/server-authority.md). Absent: judged as is.
       */
      ack?: number;
    }
  /** A jump starts (docs/stamina.md): it costs stamina, and none is allowed at 0. */
  | { t: "jump" }
  | { t: "target"; id: number | null }
  /**
   * `aim`: a projectile ability cast with no target flies this way (yaw `ry`, pitch `p`, rad;
   * docs/combat.md "Projectiles"); the server fires it from its own position of the player.
   */
  | { t: "cast"; ability: string; target: number | null; aim?: { ry: number; p: number } }
  | { t: "stopcast" }
  /** Asks for the Bestiary (answered with a `bestiary` message). */
  | { t: "bestiary" }
  /** A new look, saved at your own Furnishing Station (docs/looks.md). */
  | { t: "look"; station: number; look: LookDef }
  /** Writes your own sign (docs/building.md "Signs"); empty text wipes it. */
  | { t: "signtext"; id: number; text: string }
  | { t: "autoattack"; on: boolean }
  | { t: "interact"; id: number } // resource node, ground item, corpse, structure, NPC (talk)
  /**
   * Gather a world scatter resource (a medium or large rock, a mushroom: docs/terrain.md "Scatter
   * resources") by its index, as `interact` gathers a node. `k`: the zone's scatterKey
   * (sim/boulders.ts); a client laid out from other content is refused.
   */
  | { t: "scatter"; i: number; k: string }
  /**
   * Horizontal axe swing (docs/lumberjack.md). `ry` is the swing heading (same convention as
   * move.ry). Takes an axe from the bags when none is in hand; hits every tree, stump, sprig, log and mob in the arc.
   */
  /** `aim`: the wood an E-press swings at (hit in any direction within swingRange, not just the arc). */
  | { t: "swing"; ry: number; aim?: number }
  /**
   * Cast a line at a fishing spot (docs/fishing.md). Takes a rod from the bags; `bait` picks the
   * bait (default: the luckiest carried). `interact` on a spot casts too, or reels while fishing.
   */
  | { t: "fish"; id: number; bait?: string }
  /** Fishing: hook the fish while it bites; before a bite it reels the line in (ends the cast). */
  | { t: "reel" }
  | { t: "pickup"; id: number }
  | {
      t: "invmove";
      from: ContainerRef;
      fromSlot: number;
      to: ContainerRef;
      toSlot: number;
      count?: number;
    }
  /** Drops a stack at your feet: out of the bags, or out of an open window (`from`, dragged out). */
  | { t: "invdrop"; slot: number; count?: number; from?: ContainerRef }
  /**
   * "Place stacks" (Valheim) in the open chest `chest`: every bag item the chest already holds
   * goes in, topping up its stacks first, then into empty slots (World.onPlaceStacks).
   */
  | { t: "placestacks"; chest: number }
  /** Your open corpse: everything that fits into the bags (World.onTakeAll). */
  | { t: "takeall"; chest: number }
  /** Wear the bag item in `slot`; `to` picks which slot it goes in when it fits more than one (a ring). */
  | { t: "equip"; slot: number; to?: EquipSlot }
  /** The tool belt (docs/tools.md): take its `kind` tool in hand, or drop it. */
  | { t: "beltequip"; kind: ToolKind }
  | { t: "beltdrop"; kind: ToolKind }
  | { t: "unequip"; slot: EquipSlot }
  | { t: "use"; slot: number }
  /**
   * Craft `count` (default 1) of a recipe. One that needs a station is queued at the nearest one in
   * range (docs/crafting.md): its inputs are taken now, it is made there in real time, and you
   * collect it there. One that doesn't is made in your hands, in order, as you play.
   */
  | { t: "craft"; recipe: string; count?: number }
  /** Take what a station made for you (also by itself while you stand by it with room). */
  | { t: "collect"; station: number }
  /**
   * Send me everything again (GB-163): the client got a dynamic part for an entity it doesn't hold
   * (it never should; a missed full view). At most one every few seconds is honoured.
   */
  | { t: "resync" }
  /**
   * Cancel one of your jobs at a station (`job`: its index): its unspent inputs come back.
   * `recipe`: the job's recipe as the client saw it; refused when that job is another by now (the
   * one before it finished on the way), so a cancel never takes off a job the player didn't pick.
   */
  | { t: "craft_cancel"; station: number; job: number; recipe?: string }
  | { t: "build"; piece: string; x: number; y: number; z: number; rot: number }
  | { t: "demolish"; id: number }
  | { t: "terraform"; x: number; z: number; mode: "raise" | "lower" | "flatten"; target?: number }
  | { t: "claimlot"; lot: string }
  /** Explicit, non-withdrawable pilot project donation. Server validates location and inventory. */
  /** Hand items to a communal quest's project (its `deliver` to `project`, at its `at`). */
  | { t: "quest_contribute"; quest: string; item: string; count: number }
  | { t: "quest_accept"; quest: string }
  | { t: "quest_turnin"; quest: string }
  | { t: "quest_abandon"; quest: string }
  /** Choose a choice of the open dialog by its index in the node's full choice list. */
  | { t: "dialog_choose"; choice: number }
  | { t: "dialog_close" }
  /** Give an NPC items from an inventory slot (docs/narrative.md). */
  | { t: "give"; npc: number; slot: number; count?: number }
  /** The Collector gives the Museum one of this bag slot's item (docs/civic.md). */
  | { t: "donate"; slot: number }
  /**
   * Chat. `ch`: "say" (default; the players around you, in your World), "zone" (everyone in your
   * zone, every layer) or "world" (everyone online). A text starting with /z or /w picks the
   * channel too.
   */
  | { t: "chat"; text: string; ch?: ChatChannel }
  /**
   * Take a zone link (atlas.json: a ferry, a pass) from where you stand: the server checks you are
   * at its `from` end and free to go, then hands you over to the other zone (a `zone` message,
   * then that zone's welcome and snapshots). docs/decisions/0011-zones-and-layers.md.
   */
  | { t: "travel"; link: string }
  | { t: "closechest" }
  | { t: "unstuck" } // stranded by terrain: put me on the nearest walkable ground (also `/stuck`)
  /**
   * My step was stopped at the zone's edge (the hard bound of the playable square, slope.ts
   * groundRules.inside): the server logs it like a refused move, says why, and out at sea lets a
   * swimmer pressing against it hold their breath (World.onEdge). At most about once a second.
   */
  | { t: "edge" }
  | { t: "respawn" }
  /**
   * Map pins (docs/map-pins.md): your own marks on this zone's map, kept with your character, shown
   * on the HUD compass. Add one at (x, z) (optionally named), remove one, or name one ("" clears).
   */
  | { t: "pin"; x: number; z: number; name?: string }
  | { t: "unpin"; id: number }
  | { t: "pinname"; id: number; name: string }
  /**
   * Arrange the action bar (sim/actionBar.ts, docs/combat.md "Action bar"): all 8 slots, each an
   * ability of your class or null, none twice.
   */
  | { t: "bar"; bar: (string | null)[] }
  /** Learn an ability from a class trainer NPC (docs/combat.md "Training"), for coin. */
  | { t: "learn"; npc: number; ability: string }
  /** `r`: the client's last measured round trip, ms (the leeway its dodges get: docs/combat.md) */
  | { t: "ping"; c: number; r?: number };

/** Chat channels (docs/decisions/0011): say per World, zone across its layers, world global. */
export type ChatChannel = "say" | "zone" | "world";

export type EntityKind =
  "player" | "mob" | "node" | "drop" | "piece" | "corpse" | "log" | "npc" | "proj";

/** Public, broadcast view of a dynamic entity. Kept small: sent ~10x/s. */
export interface EntityView {
  id: number;
  k: EntityKind;
  d: string; // def id: class id, creature id, resource id, item id, piece id
  n?: string; // display name; a sign: its text
  /** A player's look when it isn't their class's (sim/looks.ts encodeLook; docs/looks.md). */
  ap?: string;
  x: number;
  y: number;
  z: number;
  ry: number;
  hp?: number;
  mhp?: number;
  lv?: number;
  tg?: number | null; // current target (mob's victim / player's target)
  st?: string; // state: idle/move/attack/cast/dead/depleted/ghost; nodes: stump; logs: fall (still on its stump); npcs: your stance (hostile/neutral/friendly); lamps: lit
  c?: number; // count for drops
  /** A cast: ability (a creature's: its name in `n`), elapsed, duration; `ch` a channel (drains). */
  cast?: { a: string; t: number; d: number; n?: string; ch?: true };
  fx?: string[]; // visible effect ids
  /** Each effect in `fx`'s stacks, in its order, when any has more than 1 (docs/combat.md). */
  fxn?: number[];
  /** A creature's break bar, 0-1 (docs/combat.md "Control"); absent when empty or none. */
  brk?: number;
  own?: number; // owner character id (pieces, corpses)
  /** An item stand's item on show (docs/building.md "Item stands"): an item id. */
  it?: string;
  open?: boolean; // doors
  boss?: boolean;
  elite?: boolean;
  tel?: { x: number; z: number; r: number; t: number }; // telegraphed AoE (remaining s)
  // Logs (k: "log", d: a resource id with a `body`), including felled trees while they fall:
  // x/y/z = centre of the trunk, ry = heading of the trunk axis (the way a felled tree falls).
  tl?: number; // tilt from vertical: 0 = upright on its stump, PI/2 = lying down
  rl?: number; // accumulated roll about the trunk's own axis (rad), for rolling visuals
  ln?: number; // trunk length (m)
  rd?: number; // trunk radius (m)
  /** A slain mob's characters with loot left on it (docs/loot.md "Loot window"): it sparkles for them. */
  lt?: number[];
  // Loose loot (k: "drop", docs/loot.md). `own` = the only character that may take it (for now).
  au?: boolean; // loose: swoops into a nearby player by itself (else picked up with E)
  /** In flight: x/y/z are the pop origin and this its velocity; mirror it with sim/loot.ts. */
  pv?: [number, number, number];
  /** Swooping into this player entity, from x/y/z, over tuning.lootSwoopTime. */
  sw?: number;
  /**
   * A player fishing (docs/fishing.md): the bobber's [x, z] (on the water surface) and the phase,
   * 0 waiting, 1 a fish bites, 2 reeling a hooked fish in (it comes out over tuning.fishReelTime).
   */
  fi?: [number, number, 0 | 1 | 2];
  /** A spectral creature (docs/town-world.md): only the Ghost Hunter is ever sent one. */
  sp?: boolean;
  /** A lit lamp (st "lit"): it burns until this epoch ms (the client counts the fuel down). */
  fu?: number;
  /**
   * A projectile in flight (k "proj", d: its look; docs/combat.md "Projectiles"): x / y / z were
   * its position at tick `tk`, `v` its velocity then, `g` its gravity (m/s² down). The client flies
   * it on from there; a homing one is re-sent when it turns. `src`: who shot it.
   */
  v?: [number, number, number];
  g?: number;
  tk?: number;
  src?: number;
}

export interface QuestProgress {
  id: string;
  progress: number[];
  done: boolean;
  /** A daily quest: the town day its progress belongs to (it clears when the day turns). */
  day?: number;
}

/** What keeps re-applying a status effect (SelfState effects `while`). */
export type EffectSource = "rain" | "fire" | "shelter";

/** Crit, haste, avoidance and resistances as the character sheet shows them: chances as shares. */
export interface FightView {
  crit: number;
  critMult: number;
  haste: number;
  hit: number;
  dodge: number;
  parry: number;
  block: number;
  blockValue: number;
  /** Only the schools with any. */
  resist: Partial<Record<SpellSchool, number>>;
  /** The share armor stops against a creature of the player's level. */
  armorShare: number;
}

export interface SelfState {
  charId: number;
  name: string;
  cls: ClassId;
  level: number;
  xp: number;
  xpNext: number;
  hp: number;
  maxHp: number;
  res: number;
  maxRes: number;
  power: number;
  armor: number;
  /** The character sheet's fight stats (docs/combat.md). */
  fight: FightView;
  /** Coin, in whole copper (docs/coin.md). */
  coin: number;
  /** s left in which a reactive ability (`requires: "avoid"`) may be used, after a dodge, parry or block. */
  reactive?: number;
  inv: Slot[];
  /** Your map pins in this zone (docs/map-pins.md). */
  pins: MapPin[];
  /** The tool belt (docs/tools.md): a tool per kind, outside the bags. */
  belt: Partial<Record<ToolKind, string>>;
  equip: Partial<Record<EquipSlot, string>>;
  /** Their look (docs/looks.md), and the cosmetics they own beyond the creation set. */
  look: LookDef;
  wardrobe: string[];
  /** The action bar's 8 slots (sim/actionBar.ts): ability ids, null for an empty slot. */
  bar: (string | null)[];
  /** The abilities they know (learned at their trainer: docs/combat.md "Training"). */
  known: string[];
  /** The learned recipes they know (docs/crafting.md "Learned recipes"). */
  recipes: string[];
  skills: Record<SkillId, { level: number; xp: number; next: number }>;
  quests: QuestProgress[];
  completed: string[];
  cooldowns: Record<string, number>; // ability -> seconds remaining
  gcd: number;
  /**
   * s of protection left (mobs don't aggro on the player) when this was sent; absent when none.
   * Ends early (a fresh self state without it) when the player attacks.
   */
  grace?: number;
  /** The protection is a corpse run's: it lasts until the corpse is recovered (or `grace` runs out). */
  graceRun?: boolean;
  /**
   * Active buffs / debuffs: `t` = s left when sent. `while` = what keeps re-applying it right
   * now (rain keeps you Wet, a fire or shelter keeps you Rested): its countdown is frozen until
   * that ends, so the HUD names the source instead of a timer. Absent when nothing sustains it.
   */
  effects: { id: string; t: number; stacks?: number; while?: EffectSource }[];
  lot: string | null;
  dead: boolean;
  ghost: boolean;
  /** Admin debug menu cheats (World.adminSetCheats); absent when off. */
  god?: true;
  /** Admin walk speed multiplier; absent = 1. The client walks at it, the server allows it. */
  speed?: number;
  /** Admin Free build (docs/ruins.md): builds on a workshop lot at no cost; absent when off. */
  freeBuild?: true;
  sliceComplete: boolean;
  chest: {
    id: number;
    slots: Slot[];
    /** The piece's name ("Chest", "Town Stockpile"). */
    name?: string;
    /** A town storage (docs/permits.md): what you may still take from it today. */
    share?: { left: number; cap: number };
    /** A town fixture's key ("town_stockpile"): its panel shows the town quest handed in there. */
    fixture?: string;
    /** Your corpse (take from it, Take all; nothing goes in). */
    corpse?: true;
    /** A slain mob's loot window, your own roll (take only, Take all; docs/loot.md). */
    loot?: true;
  } | null;
  crafting: { recipe: string; t: number; d: number } | null;
  /**
   * Your line in the water (docs/fishing.md): the spot entity, the bait on the hook and the phase;
   * `t` = s left in this phase when sent and `d` its full length (bite: the hook window, which
   * fishing skill widens; reel: until it lands). Absent
   * when not fishing. What is on the hook is never sent: it is revealed when it lands.
   */
  fishing?: { spot: number; bait: string; phase: "wait" | "bite" | "reel"; t?: number; d?: number };
  /** Reputation by faction id (only factions you have any standing with). */
  rep: Record<string, number>;
  /** Quest-board quests you can accept now (the server decides: facts, flags, exclusions). */
  board: string[];
  /** In deep water (docs/terrain.md): slower, no abilities, gathering or building. */
  swimming: boolean;
  /** Swimming with the head under the surface: breath drains (never at the surface). */
  underwater?: boolean;
  /**
   * The town permit you hold (docs/town-world.md: "lamplighter", "ghost_hunter"…); absent: none.
   * The client mirrors what it allows (a Lamplighter's lamp ghost in town).
   */
  permit?: string;
  /**
   * s of breath when this was sent (full and the refill rate: `swimPerks` at your swimming level).
   * It drains 1/s while `underwater` and refills otherwise: the client runs the meter on from here.
   */
  breath: number;
  /**
   * Your queues at this zone's crafting stations (docs/crafting.md): the station's entity id and
   * piece, the jobs left (head first), when the head job's current run started (epoch ms) and
   * what is waiting for you there.
   */
  queues: {
    station: number;
    piece: string;
    jobs: { recipe: string; left: number }[];
    at: number;
    /** the server's clock (epoch ms) when this was sent: the HUD times the run from here */
    now: number;
    /** the output slots are full: the station waits for you to collect */
    full?: true;
    out: ItemStack[];
  }[];
  /**
   * Stamina when this was sent, of `staminaMax` (docs/stamina.md), and whether you are winded (no
   * sprinting until it is back to staminaSprintMin). Sent when it starts or stops changing, runs
   * out or fills up, and after each lump spent; the client predicts it in between.
   */
  stamina: number;
  staminaMax: number;
  winded: boolean;
  /**
   * Seconds since your last stamina spend when this was sent (capped at 60): regen waits
   * staminaRegenDelay after it, and the client's prediction waits for the server's spends too.
   */
  staminaIdle: number;
  /** The food stomach (docs/food.md): each food and its seconds of play left when this was sent. */
  stomach: { item: string; left: number }[];
}

/** The town board (docs/permits.md) as one player sees it: sent when the roster changes. */
export interface TownView {
  /** Town day number; resets at `resetAt` (ms since the epoch). */
  day: number;
  resetAt: number;
  /** Town Points: the town's total, and yours. */
  town: number;
  you: number;
  top: { name: string; points: number }[];
  permits: {
    id: string;
    name: string;
    blurb: string;
    holder: string | null;
    /** Duties the holder finished today, of `total`. */
    done: number;
    total: number;
    /** Whole days since the holder last finished a duty (absent: 0). */
    idle?: number;
    /** The permit's one-off projects, in order (`done`: finished for good). */
    projects?: { progress: number; count: number; done: boolean }[];
  }[];
  /** Your permit and today's duties, if you hold one. */
  mine: {
    permit: string;
    /** Where the work lies (PermitDef.where). */
    where?: string;
    duties: {
      text: string;
      progress: number;
      count: number;
      points: number;
      done: boolean;
      /** A multi-day project: progress carries over and it pays once. */
      project?: boolean;
      /** Not open yet (DutyDef.opens: the Collector's wait for the Museum). */
      closed?: boolean;
    }[];
  } | null;
  /** What you may still take from each town storage today. */
  takes: { storage: string; name: string; left: number; cap: number }[];
  recent: string[];
  /** The permit office is closed (its saved state couldn't be read): permits are paused. */
  closed?: true;
  /** The town's public building projects (docs/civic.md): what stands of each, need by need. */
  civic?: {
    id: string;
    name: string;
    done: boolean;
    /** being built now (the one before it is done) */
    open: boolean;
    needs: { label: string; have: number; count: number }[];
  }[];
}

/** The open conversation with an NPC, as the server sees it (docs/narrative.md). */
export interface DialogView {
  /** NPC entity id */
  npc: number;
  /** NPC def id */
  id: string;
  name: string;
  title?: string;
  /** your stance with the NPC (from its faction reputation) */
  stance: "hostile" | "neutral" | "friendly";
  faction?: { id: string; name: string; rep: number };
  text: string;
  /** visible choices; `i` is the index to send back in dialog_choose */
  choices: { i: number; text: string }[];
  /** quests this NPC gives: offered (can accept), active, ready (can turn in) */
  quests: { id: string; state: "offer" | "active" | "ready" }[];
  /** a class trainer: the class they teach (the dialog offers training to that class) */
  trainer?: ClassId;
}

/**
 * A Bestiary line (docs/rare-beasts.md): a creature the character has met or killed, or a rare
 * beast of their zone still unkilled, shown as a silhouette (no name or level) with its hint.
 */
export interface BestiaryEntry {
  id: string;
  name: string | null;
  level: number | null;
  kills: number;
  seen: boolean;
  trophy: boolean;
  rare?: { tier: RareTier; hint: string };
}

export interface WorldInfo {
  time: number; // 0..1 day fraction (0.25 = sunrise... 0.5 noon)
  weather: Weather;
  night: boolean;
}

export interface CombatEvent {
  src: number;
  dst: number;
  amt: number;
  kind: "dmg" | "heal" | "miss" | "absorb";
  ab?: string;
  crit?: boolean;
  /** A "miss" event: how the blow failed (docs/combat.md); a spell's miss is a resist. */
  avoid?: "miss" | "dodge" | "parry" | "resist" | "immune" | "interrupted";
  /** A "dmg" event: what a block stopped (`amt` is what got through). */
  blocked?: number;
  /** Axe swings: how many targets this swing hit (each took a split share, tuning.swingSplit). */
  sp?: number;
  /** Short float label shown instead of the number, e.g. "Too hard" (axe tier too low). */
  note?: string;
  /** Damage from a falling or rolling log (src is the log entity), not a weapon. */
  crush?: boolean;
  /** Damage from the environment, not an entity (src 0): drowning (docs/terrain.md). */
  env?: "drowning";
}

/**
 * The wire's compatibility number (docs/deploy.md "Clients follow the server"). Bump it by hand on
 * any change an older client can't handle (a message it would misread, a required field). Web
 * clients follow the server's exact build instead; a native build (its web assets bundled) may
 * play a different build only while this matches.
 */
export const PROTOCOL_VERSION = 3;
/**
 * The oldest protocol this server still serves (each socket gets its own format, GB-163): a
 * native build on a protocol from MIN_PROTOCOL to PROTOCOL_VERSION plays on.
 */
export const MIN_PROTOCOL = 1;

/**
 * The fields of a player's or creature's view that change as it moves and fights (GB-163 step 2):
 * a client that holds an earlier version of the view gets only those of these that changed since
 * (a ViewPart), while the rest stays the same.
 */
export const VIEW_DYNAMIC = [
  "x",
  "y",
  "z",
  "ry",
  "hp",
  "tg",
  "st",
  "cast",
  "fx",
  "fxn",
  "brk",
  "tel",
  "fi",
] as const;
type DynamicField = (typeof VIEW_DYNAMIC)[number];
const DYNAMIC_SET: ReadonlySet<string> = new Set(VIEW_DYNAMIC);
/** Of those, the ones a view may leave out: a part says so with null (tg's null is its own). */
const VIEW_DYNAMIC_OPTIONAL: ReadonlySet<string> = new Set([
  "hp",
  "cast",
  "fx",
  "fxn",
  "brk",
  "tel",
  "fi",
]);

/**
 * What changed in a view's dynamic fields from one version to the next: its id and each field that
 * changed, one that went away as null (tg's null is its own value), so the merge never guesses.
 */
export type ViewPart = { id: number } & { [K in DynamicField]?: EntityView[K] | null };

const same = (a: unknown, b: unknown) =>
  a === b || (typeof a === "object" && a !== null && JSON.stringify(a) === JSON.stringify(b));

/** The part that turns `from` into `to` (same entity, same static key). */
export function viewDelta(from: EntityView, to: EntityView): ViewPart {
  const out: Record<string, unknown> = { id: to.id };
  for (const f of VIEW_DYNAMIC) {
    const a = from[f];
    const b = to[f];
    if (!same(a, b)) out[f] = b ?? null;
  }
  return out as ViewPart;
}

/** The rest of a view (what a part doesn't carry), as a key that changes when any of it does. */
export function viewStaticKey(v: EntityView): string {
  const out: Record<string, unknown> = {};
  for (const k in v) if (!DYNAMIC_SET.has(k)) out[k] = v[k as keyof EntityView];
  return JSON.stringify(out);
}

/**
 * Position and heading as whole centimetres and hundredths of a radian (GB-163 step 3). Views
 * carry them rounded to that already, so nothing is lost: the client's own position, which the
 * predictor reconciles against, is exactly what it was.
 */
const QUANTIZED = ["x", "y", "z", "ry"] as const;

export function quantizePart(p: ViewPart): ViewPart {
  const out: Record<string, unknown> = { ...p };
  for (const f of QUANTIZED) {
    const x = p[f];
    if (typeof x === "number") out[f] = Math.round(x * 100);
  }
  return out as ViewPart;
}

export function unquantizePart(p: ViewPart): ViewPart {
  const out: Record<string, unknown> = { ...p };
  for (const f of QUANTIZED) {
    const x = p[f];
    if (typeof x === "number") out[f] = x / 100;
  }
  return out as ViewPart;
}

/** A held view with a part over it: what the server's full view is (the client's net.ts). */
export function mergeViewPart(old: EntityView, part: ViewPart): EntityView {
  const out: Record<string, unknown> = { ...old };
  for (const f of VIEW_DYNAMIC) {
    const x = part[f];
    if (x === undefined) continue;
    if (x === null && VIEW_DYNAMIC_OPTIONAL.has(f)) delete out[f];
    else out[f] = x;
  }
  return out as unknown as EntityView;
}

export type ServerMsg =
  | {
      t: "welcome";
      you: number;
      charId: number;
      /**
       * The player's latest correction number (the move fence, docs/server-authority.md): a
       * resumed session keeps its server entity, and the client acks from here.
       */
      n: number;
      /**
       * The server's build (its commit) when it requires clients of the same build
       * (REQUIRE_SAME_BUILD): a web client on another build reloads into this one.
       */
      build?: string;
      /**
       * The server's PROTOCOL_VERSION and the oldest it still serves (MIN_PROTOCOL): a native build
       * outside that range needs the store's update.
       */
      protocol?: number;
      minProtocol?: number;
      contentVersion: number;
      terrain: { k: number; d: number }[];
      lots: Record<string, number | null>;
      lotNames: Record<string, string>;
    }
  | {
      t: "snap";
      tick: number;
      ents: EntityView[];
      /**
       * Protocol 2+ (GB-163): players and creatures the client already holds whole, as their
       * dynamic part only (ViewPart): merged over the held view (mergeViewPart). Applied after
       * `ents`, before `gone`.
       */
      parts?: ViewPart[];
      /**
       * Protocol 3+ (GB-163 step 3): `parts` with x / y / z in centimetres and ry in hundredths of
       * a radian, as integers (the views' own precision: r2), turned back by `unquantizePart`.
       */
      qparts?: ViewPart[];
      gone: number[];
      world: WorldInfo;
      full?: boolean;
    }
  | { t: "self"; s: SelfState }
  | { t: "combat"; ev: CombatEvent[] }
  | { t: "terrain"; d: { k: number; d: number }[] }
  | { t: "lots"; lots: Record<string, number | null>; lotNames: Record<string, string> }
  | { t: "town"; v: TownView }
  | {
      t: "townquest";
      v: TownQuestsView | null;
      zone: string;
      unlockedPlots: string[] | null;
      housing: TownHousing | null;
    }
  /**
   * The Museum's collection (docs/civic.md), on opening a display case or after a donation:
   * whether the Museum stands, what it has (item id → who gave it) and how much there is to collect.
   */
  | { t: "museum"; open: boolean; have: Record<string, string>; total: number }
  | { t: "bestiary"; entries: BestiaryEntry[] }
  /** Your Furnishing Station opened: the look editor, for it (docs/looks.md). */
  | { t: "look_open"; station: number }
  /** Your own sign: the editor, for it (docs/building.md "Signs"). Others read it (a `msg`). */
  | { t: "sign_open"; id: number }
  | {
      t: "msg";
      text: string;
      kind: "info" | "error" | "loot" | "quest" | "level" | "chat" | "system";
      from?: string;
      /** chat: the channel (absent: say) */
      ch?: ChatChannel;
      /** error: the intent it refuses, where the client must react (today "travel") */
      re?: "travel";
      /** loot: what was received (drop pickups, gather yields), for the floating "+n Item" */
      got?: { item: string; n: number };
    }
  /**
   * You are (now) in this zone (docs/decisions/0011-zones-and-layers.md). Sent on every attach
   * before the zone's welcome and first snapshot: on login, and when a handoff moves you to
   * another zone. The client loads that zone's content (`/v1/content?zone=`) when it doesn't
   * have this hash, and rebuilds the world around the new zone. `via`: the link that brought you
   * (absent on login); the client shows its kind's transition. Nothing in it assumes the zones
   * share a process: a gateway may route the socket to another zone server after it.
   */
  | {
      t: "zone";
      zone: ZoneId;
      name: string;
      contentHash: string;
      origin: { x: number; z: number };
      size: number;
      via?: { link: string; kind: ZoneLink["kind"] };
      /** Your corpse (and your bag) lies in this other zone (its name); the HUD says so. */
      corpseIn?: string;
      /**
       * Which layer of the zone (docs/decisions/0011, "Layers": overflow copies sharing the
       * zone's structures). Absent from servers before layers.
       */
      layer?: number;
      /**
       * Moved to another layer of the same zone: same content, same spot. The client keeps its
       * view, shows a brief shimmer and takes the new layer's welcome (which drops every entity
       * of the old one) and snapshots.
       */
      shift?: true;
    }
  | {
      t: "fx";
      kind: string;
      x: number;
      y: number;
      z: number;
      src?: number;
      dst?: number;
      /** m: a ground area's radius (`area`). */
      r?: number;
    }
  /**
   * The world scatter's mined-out resources changed (docs/terrain.md "Scatter resources"): `gone`
   * are mined out (the client hides them), `back` grew back. `full`: `gone` is every one
   * mined out right now (after the welcome, or new content); forget the rest.
   */
  | { t: "scatter"; gone: number[]; back: number[]; full?: true }
  | { t: "pong"; c: number; s: number }
  /**
   * Everyone playing in your zone (layer), about once a second, for the map: entity id, name and
   * position (to 0.1 m), you among them. Snapshots stop at interestPlayer; the map shows the rest.
   * Empty once when you are left alone (then none until someone else comes).
   */
  | { t: "roster"; ps: [id: number, name: string, x: number, z: number][] }
  /**
   * Authoritative position correction for the receiving player (rejected move, respawn, teleport).
   * Always snap to it. `relocated` (teleport, respawn): the server dropped the target; clear it too.
   */
  | {
      t: "correct";
      x: number;
      y: number;
      z: number;
      relocated?: true;
      /** A refused move put the player back (counted as a snap-back in the session summary). */
      refused?: true;
      /** The correction's number: the client sends it back as `ack` on its next moves. */
      n: number;
    }
  /** Content changed live (admin edit): refetch /content (hash = the new content hash). */
  | { t: "content"; hash: string }
  | { t: "kicked"; reason: string }
  /** The open dialog changed (null: closed). */
  | { t: "dialog"; d: DialogView | null };
